import { ClientActionCacheService } from "@cervval.js/client-action/src/services/client.action.cache.service";
import { Action } from "@cervval.js/common-action/src/action.model";
import { ACTION_SERVICE, ActionService } from "@cervval.js/common-action/src/services/action.service";
import { ACTION_CACHE_SERVICE } from "@cervval.js/common-action/src/services/cache.service";
import { DTO_MODEL_SERVICE, DTOModelService } from "@cervval.js/common-dto/src/dtoModel.service";
import { injector } from "@cervval.js/common/src/dependencies/injector";
import { DataModel } from "@cervval.js/common/src/forms/dataModel";
import { objectKeys } from "@cervval.js/common/src/tools/object.tools";
import { BaseElementTypes } from "@ehealth_tools/common/src/data/dto/base.dto";

interface GetDataModelOptions<ElementTypes, TableName extends keyof ElementTypes> {
    /** Callback called during action callback (eg. In encapsulateInAction(action => { .... })) */
    inActionCallback?: {
        onCreate?: (processedElement: ElementTypes[TableName], action: Action<ElementTypes, unknown, unknown>) => void,
        onUpdate?: (processedElement: ElementTypes[TableName], action: Action<ElementTypes, unknown, unknown>) => void,
        onDelete?: (processedElement: ElementTypes[TableName], action: Action<ElementTypes, unknown, unknown>) => void
    };
    afterActionCallbacks?: {
        onCreate?: () => void,
        onUpdate?: () => void,
        onDelete?: () => void
    };
}
/** */
export function getDataModel<ElementTypes extends BaseElementTypes, TableName extends keyof ElementTypes & string>(table: TableName, options?: GetDataModelOptions<ElementTypes, TableName>): DataModel<ElementTypes[TableName]> {
    const dm = new DataModel<ElementTypes[TableName]>(table);
    const persistenceService = injector.get<ActionService<ElementTypes, unknown>>(ACTION_SERVICE);
    const cacheService = injector.get<ClientActionCacheService<ElementTypes, unknown>>(ACTION_CACHE_SERVICE);

    dm.setData(cacheService.getDTOCacheRepository(table).dtoList);

    dm.onCreate = async (items) => {
        await persistenceService.encapsulateInAction(action => {
            for (const item of items) {
                item.__tableName = table;
                _dataModelConditionalCreate(action, table, item, options?.inActionCallback?.onCreate);
            }
        }, {
            logMessage: "Création de " + items.length + " " + table
        });
        options?.afterActionCallbacks?.onCreate?.();
    };

    dm.onDelete = async (items) => {
        const buildOperationsPromise: ((action: Action<ElementTypes, unknown, unknown>) => void)[][] = [];
        for (const item of items) {
            const pToPush = _dataModelConditionalDeleteByOperations(table, item, options?.inActionCallback?.onDelete);
            buildOperationsPromise.push(pToPush);
        }

        await Promise.all(buildOperationsPromise).then((operationsList) => {
            return persistenceService.encapsulateInAction(action => {
                for (const operation of operationsList.flat()) {
                    operation(action);
                }
            }, {
                logMessage: "Suppression de " + items.length + " " + table
            });
        });
        options?.afterActionCallbacks?.onDelete?.();
    };

    dm.onUpdate = async (items, propertyName, value) => {

        await persistenceService.encapsulateInAction(action => {
            _dataModelConditionalUpdate(action, table, items, propertyName, value, options);
        }, {
            logMessage: "Modification de " + items.length + " " + table
        });
        options?.afterActionCallbacks?.onUpdate?.();
    };

    return dm;
}

/** create item and process given associations fields */
function _dataModelConditionalCreate<ElementTypes extends BaseElementTypes, TableName extends keyof ElementTypes & string>(action: Action<ElementTypes, unknown, unknown>, table: TableName, item: ElementTypes[TableName], inActionCallback?: (processedElement: ElementTypes[TableName], action: Action<ElementTypes, unknown, unknown>) => void): void {
    const tableAssociations = injector.get<DTOModelService<ElementTypes, unknown>>(DTO_MODEL_SERVICE).getTableAssociations(table);
    const tableAssociationFields = objectKeys(tableAssociations);
    const doesDTOHasAssociationFields = tableAssociationFields.length > 0;

    action.create(table, item);
    inActionCallback?.(item, action);
    if (doesDTOHasAssociationFields) {
        for (const tableAssociationField of tableAssociationFields) {
            // Get the association Id(s) from the item object (eg. itemA._itemBIds) since they are not handled at creation
            const associationIds = item[tableAssociationField];
            if (associationIds == null || Array.isArray(associationIds) && associationIds.length === 0) { continue; }
            const associationList = Array.isArray(associationIds) ? [...associationIds] : [associationIds];
            // Clear the associative field of the item to allow addToArray action to detect that associations changed (it will fill it)
            delete item[tableAssociationField];
            for (const newAssociation of associationList) { action.addToArray(table, item, tableAssociationField, { addReference: { id: newAssociation } }, false); }

        }
    }
}

/** delete item and process associations, to cascade deletion if needed */
function _dataModelConditionalDeleteByOperations<ElementTypes extends BaseElementTypes, TableName extends keyof ElementTypes & string>(table: TableName, item: ElementTypes[TableName], inActionCallback?: (processedElement: ElementTypes[TableName], action: Action<ElementTypes, unknown, unknown>) => void): ((action: Action<ElementTypes, unknown, unknown>) => (void))[] {
    const operations: ((action: Action<ElementTypes, unknown, unknown>) => (void))[] = [];
    operations.push((action: Action<ElementTypes, unknown, unknown>) => {
        inActionCallback?.(item, action);
        action.delete(table, item);
    });
    return operations;
}

/** select and process action according to the associative nature of updated field (update if direct, add / remove from array if associative) */
function _dataModelConditionalUpdate<ElementTypes extends BaseElementTypes, TableName extends keyof ElementTypes & string>(action: Action<ElementTypes, unknown, unknown>, table: TableName, items: ElementTypes[TableName][], propertyName: keyof ElementTypes[TableName], value: ElementTypes[TableName][keyof ElementTypes[TableName]], options?: GetDataModelOptions<ElementTypes, TableName>): void {

    for (const item of items) {

        // Handle conditional update if needed here ... with continue

        action.updateValueIfNeeded(table, item, propertyName, value);
        options?.inActionCallback?.onUpdate?.(item, action);
    }
    return;
}